import React from 'react';
import LegalPage from '../components/LegalPage';

const TERMS_HTML = `
  <div class="meta">
    <strong>Effective:</strong> May 29, 2026 &nbsp;·&nbsp;
    <strong>Entity:</strong> GoogLyn AI Inc. &nbsp;·&nbsp;
    <strong>Jurisdiction:</strong> Ontario, Canada &nbsp;·&nbsp;
    <strong>Version:</strong> 4.0
  </div>

  <div class="summary">
    <div class="label">Plain English — What You're Agreeing To</div>
    <h2>The essentials</h2>
    <ul>
      <li>Use our AI services responsibly and lawfully.</li>
      <li>You own your content. You own the outputs we create for you.</li>
      <li>We never use your data to train AI. We never sell your data.</li>
      <li>Payments are processed securely. Refunds are available within 7 days for undelivered services.</li>
      <li>We're transparent: our AI is powered in part by Anthropic.</li>
      <li>Don't rely on AI outputs for medical, legal, or financial decisions without professional review.</li>
      <li>Ontario law governs. Disputes go to arbitration. Consumer rights under Ontario's CPA are fully preserved.</li>
    </ul>
  </div>

  <h2>1. Who We Are</h2>
  <p>GoogLyn AI Inc. is an Ontario corporation (BN: 733615561, OCN: 1001361399) providing AI-powered products and services including AI Concierge, Broadcast Ready Commercials, AI Application Builder, and Enterprise AI Solutions (the <strong>"Services"</strong>). "GoogLyn AI," "we," "us," and "our" refer to GoogLyn AI Inc.</p>
  <p>By accessing or using the Services, you (<strong>"User," "you," "your"</strong>) agree to be bound by these Terms of Service (<strong>"Terms"</strong>). If you do not agree, do not use the Services.</p>

  <h2>2. Who These Terms Apply To</h2>
  <ul>
    <li><strong>Visitors</strong> — browsing our website without an account;</li>
    <li><strong>Registered Users</strong> — individuals with a GoogLyn AI account;</li>
    <li><strong>Paying Customers</strong> — consumers and businesses who have purchased Services;</li>
    <li><strong>Enterprise Clients</strong> — organizations with a signed Master Services Agreement (MSA) or Order Form.</li>
  </ul>
  <div class="box box-info">
    <p><strong>Enterprise clients:</strong> If you have a signed MSA or Order Form, that agreement governs. These website Terms apply only to matters not addressed in your signed agreement. In case of conflict, your signed agreement prevails.</p>
  </div>

  <h2>3. Eligibility</h2>
  <p>You must be at least <strong>18 years of age</strong> and legally capable of entering a binding contract. If using the Services on behalf of a company, you represent that you are authorized to bind that entity.</p>

  <h2>4. Account Registration</h2>
  <p>Where registration is required, you agree to provide accurate, current information and keep it up to date. You are responsible for all activity under your account. Notify us immediately at <a href="mailto:solutions@googlyn.ca">solutions@googlyn.ca</a> if you suspect unauthorized use. We may suspend or terminate accounts that violate these Terms.</p>

  <h2>5. Our Services</h2>
  <ul>
    <li><strong>AI Concierge</strong> — AI-powered virtual engagement, customer support, and personal assistant solutions;</li>
    <li><strong>Broadcast Ready Commercials</strong> — AI-generated video production for broadcast and digital distribution;</li>
    <li><strong>AI App Builder</strong> — Custom AI application development, integration, and deployment;</li>
    <li><strong>Enterprise Solutions</strong> — Tailored AI integration and automation for organizations.</li>
  </ul>
  <p>We reserve the right to modify, suspend, or discontinue any Service with reasonable advance notice where practicable.</p>

  <h2>6. Payments, Billing, and Refunds</h2>

  <h3>6.1 Pricing and Currency</h3>
  <p>All fees are stated in <strong>Canadian dollars (CAD)</strong> unless otherwise specified and are inclusive of applicable taxes where required. Prices displayed on our website are the prices you pay — no hidden fees.</p>

  <h3>6.2 Payment Processing</h3>
  <p>Payments are processed securely through our third-party payment processor. By providing payment information, you authorize us to charge the applicable fees. We use industry-standard encryption and PCI-DSS compliant payment infrastructure. <strong>We never store your full payment card number</strong> — this is handled entirely by our payment processor.</p>

  <h3>6.3 Accepted Payment Methods</h3>
  <p>We accept major credit cards and debit cards via our website checkout. Additional payment methods may be available and will be displayed at checkout.</p>

  <h3>6.4 Billing and Receipts</h3>
  <p>Upon successful payment, you will receive an email receipt at the address associated with your account. For subscription services, you will be billed automatically on your renewal date. We will notify you at least 7 days before any price change takes effect on a subscription.</p>

  <h3>6.5 Taxes</h3>
  <p>Applicable Canadian federal and provincial taxes (including HST/GST) are calculated and added at checkout based on your billing address. You are responsible for any taxes applicable in your jurisdiction beyond what we collect.</p>

  <h3>6.6 Refund Policy</h3>
  <div class="box box-green">
    <p><strong>Our commitment to you:</strong> We want you to be satisfied with GoogLyn AI. Our refund policy is as follows:</p>
    <ul>
      <li><strong>7-day refund window:</strong> If you are not satisfied with a Service, you may request a full refund within 7 days of purchase, provided the Service has not been substantially delivered or consumed;</li>
      <li><strong>Delivered services:</strong> For Services that have been fully delivered (e.g., a completed AI commercial), refunds are assessed case-by-case based on delivery quality and your specific concerns;</li>
      <li><strong>Subscription cancellations:</strong> You may cancel a subscription at any time. Cancellation takes effect at the end of the current billing period. We do not provide partial-period refunds for subscriptions unless required by law;</li>
      <li><strong>Technical issues:</strong> If a technical failure on our end prevents delivery of a Service you paid for, you will receive a full refund or credit at your choice.</li>
    </ul>
    <p>To request a refund, contact us at <a href="mailto:solutions@googlyn.ca">solutions@googlyn.ca</a> with your order details. We respond within 2 business days.</p>
  </div>

  <h3>6.7 Chargebacks</h3>
  <p>If you initiate a chargeback with your bank or card issuer without first contacting us to resolve the issue, we reserve the right to suspend your account pending resolution. We encourage you to contact us first — we are committed to resolving disputes fairly and quickly.</p>

  <h3>6.8 Late Payments (Enterprise)</h3>
  <p>For enterprise invoiced accounts, overdue amounts accrue interest at 2% per month applied on unpaid principal balances without compounding on previously accrued interest, consistent with Ontario commercial law.</p>

  <h2>7. Acceptable Use</h2>
  <p>You agree to use the Services only for lawful purposes. You must not:</p>
  <ul>
    <li>Generate or distribute illegal, harmful, defamatory, or fraudulent content;</li>
    <li>Violate any applicable federal, provincial, or local law;</li>
    <li>Infringe any intellectual property, privacy, or other rights of any person;</li>
    <li>Attempt unauthorized access to our systems;</li>
    <li>Use automated tools without our prior written consent;</li>
    <li>Reverse engineer or decompile any component of the Services;</li>
    <li>Use Outputs to train, fine-tune, or develop competing AI models;</li>
    <li>Impersonate any person, brand, or entity;</li>
    <li>Submit inputs containing personal data of third parties without their consent;</li>
    <li>Circumvent any security or access control measures.</li>
  </ul>

  <h2>8. AI Inputs, Outputs, and Responsible Use</h2>

  <h3>8.1 Your Responsibility for Inputs</h3>
  <p>You are solely responsible for the content, accuracy, and legality of any inputs you submit. Do not submit inputs containing personal information of third parties without consent, confidential information of other organizations, or content that violates any law.</p>

  <h3>8.2 Nature of AI Outputs</h3>
  <p>AI-generated Outputs are produced by probabilistic models and may be inaccurate, incomplete, biased, or outdated. <strong>You must independently review and verify all Outputs before using them for any purpose.</strong></p>

  <h3>8.3 Prohibited Reliance — High-Risk Decisions</h3>
  <div class="box box-warn">
    <p><strong>Do not rely on GoogLyn AI Outputs — without independent professional review — for:</strong></p>
    <ul>
      <li>Medical diagnosis, treatment, or health advice;</li>
      <li>Legal advice, contracts, or regulatory compliance;</li>
      <li>Financial, investment, or tax decisions;</li>
      <li>Safety-critical engineering or infrastructure;</li>
      <li>Any regulated professional service.</li>
    </ul>
  </div>

  <h3>8.4 Output Compliance</h3>
  <p>You are solely responsible for ensuring Outputs comply with all applicable laws, advertising standards, and third-party rights when used in client deliverables, advertising, or regulated contexts.</p>

  <h3>8.5 Residual and Aggregated Data</h3>
  <p>We may use anonymized, de-identified, or aggregated data — from which no individual can reasonably be identified — for operational improvements and analytics. This does not constitute use of your personal information or content.</p>

  <h2>9. Intellectual Property</h2>

  <h3>9.1 Our IP</h3>
  <p>All rights in the Services — including software, technology, branding, trademarks, and methodologies — are owned by GoogLyn AI Inc. or its licensors. These Terms grant you no ownership in the Services.</p>

  <h3>9.2 Your Content</h3>
  <p>You retain full ownership of all content you submit. You grant GoogLyn AI a limited, non-exclusive, royalty-free licence to process your content solely to provide the Services to you. This licence terminates when you stop using the Services or request deletion.</p>

  <h3>9.3 AI Outputs — Ownership</h3>
  <p>Subject to your compliance with these Terms and payment of applicable fees, GoogLyn AI assigns to you all assignable rights in Outputs generated specifically for you. You acknowledge that AI-generated content may have limited copyright protection under current Canadian law and that similar Outputs may be generated for other users.</p>

  <h3>9.4 No AI Training on Your Data</h3>
  <div class="box box-dark">
    <p><strong>We do not use your content, inputs, prompts, or Outputs to train, fine-tune, or improve any AI model</strong> — ours or any third party's. Your data is used exclusively to provide the Services you have requested.</p>
  </div>

  <h3>9.5 GoogLyn AI IP Indemnity</h3>
  <p>GoogLyn AI will defend you against any third-party claim that the Services (excluding your inputs) infringe a Canadian intellectual property right, provided you promptly notify us, give us control of the defence, and cooperate reasonably.</p>

  <h3>9.6 Feedback</h3>
  <p>Any feedback or suggestions you provide become the property of GoogLyn AI. We may use feedback without restriction or compensation.</p>

  <h2>10. Third-Party AI — Transparency</h2>
  <div class="box box-warn">
    <p>Our Services are powered in part by third-party AI models and APIs, including those provided by <strong>Anthropic, PBC</strong>. Your inputs are transmitted to and processed by Anthropic to generate Outputs. GoogLyn AI is the <strong>data controller</strong> responsible for your personal information. Anthropic acts as a <strong>data processor</strong> under our instructions and a data processing agreement.</p>
  </div>

  <h2>11. Confidentiality</h2>
  <p>Each party agrees to maintain in confidence any non-public proprietary information disclosed by the other party, using it only as necessary under these Terms. This obligation does not apply to information that is publicly known, independently developed, or required to be disclosed by law.</p>

  <h2>12. Warranties and Disclaimer</h2>
  <p>THE SERVICES ARE PROVIDED <strong>"AS IS"</strong> AND <strong>"AS AVAILABLE."</strong> TO THE MAXIMUM EXTENT PERMITTED BY ONTARIO LAW, GOOGLYN AI DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, AND NON-INFRINGEMENT.</p>
  <p>Nothing in this section limits any statutory rights you may have under Ontario's <em>Consumer Protection Act, 2002</em> or other applicable consumer protection legislation that cannot be waived by contract.</p>

  <h2>13. Limitation of Liability</h2>

  <h3>Tier 1 — Excluded losses:</h3>
  <p>To the fullest extent permitted by law, GoogLyn AI is not liable for loss of profits, revenue, data, business opportunity, goodwill, or any indirect, incidental, special, consequential, or punitive damages.</p>

  <h3>Tier 2 — Cap on direct damages (consumer / B2C):</h3>
  <p>Our total liability to you for all claims in any 12-month period shall not exceed the greater of: (a) the total fees paid by you in the preceding 12 months; or (b) <strong>CAD $2,500.00</strong>. This cap is set at a level intended to be enforceable under Ontario's <em>Consumer Protection Act, 2002</em>.</p>

  <h3>Tier 3 — Enterprise clients:</h3>
  <p>Liability caps for enterprise clients are governed by the applicable signed MSA or Order Form.</p>

  <h2>14. Indemnification</h2>
  <h3>14.1 You indemnify us:</h3>
  <p>You agree to defend, indemnify, and hold harmless GoogLyn AI Inc. and its directors, officers, employees, and agents from claims arising from: (a) your use of the Services in breach of these Terms; (b) your violation of any law or third-party rights; or (c) content you submit. This indemnity does not apply where the claim arises from GoogLyn AI's own negligence or misconduct.</p>
  <h3>14.2 We indemnify you (IP):</h3>
  <p>GoogLyn AI will indemnify you against third-party claims that our Services (excluding your inputs) infringe Canadian intellectual property rights, as described in Section 9.5.</p>

  <h2>15. Termination</h2>
  <p>Either party may terminate at any time. We may suspend or terminate your access immediately for material breach. Upon termination, your right to use the Services ends. Sections 9, 11, 12, 13, 14, 16, and 17 survive termination. If we terminate without cause, we will refund any prepaid fees for undelivered Services.</p>

  <h2>16. Governing Law and Dispute Resolution</h2>
  <p>These Terms are governed by the laws of the <strong>Province of Ontario</strong> and the federal laws of Canada applicable therein.</p>
  <p>Disputes shall first be submitted to good-faith negotiation (30 days written notice). Unresolved disputes shall be resolved by binding arbitration in Ontario under the <em>Arbitration Act, 1991</em> (Ontario), before a single mutually agreed arbitrator. Arbitration shall be conducted in English in Ontario. Emergency injunctive relief may be sought from an Ontario court.</p>
  <div class="box box-info">
    <p><strong>Consumer Rights:</strong> Nothing in these Terms limits your rights under Ontario's <em>Consumer Protection Act, 2002</em> or other mandatory consumer protection legislation. Consumer class proceedings rights are preserved to the extent they cannot be waived under applicable Ontario law.</p>
  </div>

  <h2>17. General</h2>
  <ul>
    <li><strong>Entire Agreement:</strong> These Terms, together with any signed MSA or Order Form, are the entire agreement between us. Signed agreements prevail in case of conflict.</li>
    <li><strong>Amendments:</strong> We will provide 30 days' notice of material changes to registered users by email. Continued use after the effective date constitutes acceptance.</li>
    <li><strong>Severability:</strong> If any provision is unenforceable, it is modified minimally to be enforceable; remaining provisions stay in effect.</li>
    <li><strong>No Waiver:</strong> Failure to enforce any provision is not a waiver.</li>
    <li><strong>Assignment:</strong> You may not assign your rights without our prior written consent. We may assign in connection with a merger or asset sale.</li>
    <li><strong>Force Majeure:</strong> Neither party is liable for delays caused by circumstances beyond their reasonable control.</li>
    <li><strong>Language:</strong> English governs. In any conflict with a translation, the English version prevails.</li>
    <li><strong>Consumer Protection:</strong> Any provision conflicting with mandatory Ontario consumer protection law is amended or severed to comply.</li>
  </ul>

  <div class="contact">
    <div class="label">Legal &amp; Billing Inquiries</div>
    <p>GoogLyn AI Inc. &nbsp;·&nbsp; Toronto, Canada</p>
    <p>Email: <a href="mailto:legal@googlyn.ca">legal@googlyn.ca</a></p>
    <p>Web: <a href="https://googlyn.ca">https://googlyn.ca</a></p>
  </div>
`;

const TermsOfService = () => (
  <LegalPage title="Terms Of Service" html={TERMS_HTML} />
);

export default TermsOfService;
